// Adapts the /umbackend/role payload to the { id, name } shape @axtria/hqi-genai and
// @axtria/hqi-configurator expect for USER_PORTFOLIO_ROLES / USER_PORTFOLIO[].profile.
// The transport shape stays unchanged so the packages' own save/patch flows keep
// working as before.

import { WORKSPACE_OWNER } from '../constants/Constantsi18';

export interface ApiRole {
  role_id: number;
  role_name: string;
  role_display_name: string;
  is_active: boolean;
}

export interface PortfolioRole {
  id: number;
  name: string;
}

// Workspace Owner is excluded to match what both call sites did off profileData
// (Home filtered id 18, InsightAgent filtered the name).
export const toPortfolioRoles = (apiRoles: any): PortfolioRole[] => {
  if (!Array.isArray(apiRoles)) return [];

  return apiRoles
    .filter((role: any) => role?.is_active && role?.role_id !== undefined)
    .map((role: any) => ({
      id: role.role_id,
      name: role.role_display_name || role.role_name,
    }))
    .filter((role: PortfolioRole) =>
      role.name?.trim().toLowerCase() !== WORKSPACE_OWNER.toLowerCase()
    );
};

// Re-points a saved portfolio role at its entry in the role master so the dropdown
// pre-selects. The package matches options on String(id), so a portfolio holding a
// legacy profile id would otherwise render blank — hence the display-name fallback.
export const resolveSelectedRole = (saved: any, roles: PortfolioRole[]): any => {
  if (!saved || !roles.length) return saved;

  const byId = roles.find((role) => String(role.id) === String(saved.id));
  if (byId) return byId;

  const savedName = String(saved.name || '').trim().toLowerCase();
  const byName = roles.find((role) => role.name?.trim().toLowerCase() === savedName);

  return byName ?? saved;
};

// preferredProfile is normally a profile id, but ProfileSwitch also matches it
// by display name, so accept either form.
export const findActiveProfile = (userInfo: any) => {
  const profiles = Array.isArray(userInfo?.profileData) ? userInfo.profileData : [];
  const preferred = userInfo?.preferredProfile;
  if (preferred === undefined || preferred === null || preferred === '') return undefined;

  const wanted = String(preferred).trim().toLowerCase();
  return profiles.find(
    (profile: any) =>
      String(profile?.id) === String(preferred) ||
      String(profile?.name ?? '').trim().toLowerCase() === wanted,
  );
};

export const isWorkspaceOwnerProfile = (userInfo: any) =>
  String(findActiveProfile(userInfo)?.name ?? '').trim().toLowerCase() ===
  WORKSPACE_OWNER.toLowerCase();

/**
 * Notifications belong to the user view (Business Analyst) only -- Workspace
 * Owner has no /artifacts or /studio routes to open, which is where a
 * notification click leads.
 *
 * Returns false while profileData is still loading: the role isn't known yet,
 * and failing closed avoids briefly opening a socket we'd have to tear down.
 */
export const canReceiveNotifications = (userInfo: any) => {
  const profile = findActiveProfile(userInfo);
  if (!profile) return false;
  return String(profile.name ?? '').trim().toLowerCase() !== WORKSPACE_OWNER.toLowerCase();
};
